import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { formatCLP, formatFecha, hoyISO, diasHasta } from '../lib/format';
import { q, crearMovimiento, recalcularPrestamo, borrarMovimientoSimple } from '../lib/vinculos';
import { useAccion } from '../lib/useAccion';
import ErrorBanner from './ErrorBanner';

function ProgressBar({ cobrado, total }) {
  const pct = total > 0 ? Math.min(100, Math.round((cobrado / total) * 100)) : 0;
  const color = pct >= 75 ? '#22c55e' : pct >= 40 ? '#f59e0b' : '#3b82f6';
  return (
    <div style={{ margin: '10px 0 4px' }}>
      <div style={{ background: 'var(--border)', borderRadius: 99, height: 8, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 99, transition: 'width 0.4s ease' }} />
      </div>
      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{pct}% cobrado</span>
    </div>
  );
}

const formVacio = () => ({ persona: '', monto: '', fecha_prestamo: hoyISO(), fecha_esperada: '', notas: '' });

function MeDeben({ userId }) {
  const [prestamos, setPrestamos] = useState([]);
  const [cobros, setCobros] = useState([]);
  const [bolsillos, setBolsillos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [cobroModal, setCobroModal] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const { saving, error, setError, run } = useAccion();

  const [form, setForm] = useState(formVacio);
  const [formCobro, setFormCobro] = useState({ monto: '', bolsillo_id: '', fecha: hoyISO(), nota: '' });
  const [editForm, setEditForm] = useState({});
  const [abonoPersona, setAbonoPersona] = useState(null); // nombre de la persona que abona
  const [formAbono, setFormAbono] = useState({ monto: '', bolsillo_id: '', fecha: hoyISO(), nota: '' });

  const loadData = useCallback(async () => {
    if (!userId) return;
    const [p, c, b] = await Promise.all([
      q(supabase.from('prestamos').select('*').eq('user_id', userId).order('created_at')),
      q(supabase.from('cobros_prestamo').select('*').eq('user_id', userId).order('fecha', { ascending: false })),
      q(supabase.from('bolsillos').select('*').eq('user_id', userId).order('created_at')),
    ]);
    setPrestamos(p || []);
    setCobros(c || []);
    setBolsillos(b || []);
  }, [userId]);

  useEffect(() => {
    loadData().catch(e => setError('Error cargando datos: ' + e.message)).finally(() => setLoading(false));
  }, [loadData, setError]);

  const getTotalCobrado = (prestamoId) =>
    cobros.filter(c => c.prestamo_id === prestamoId).reduce((s, c) => s + Number(c.monto || 0), 0);

  const getPendiente = (prestamo) =>
    Math.max(0, prestamo.monto - getTotalCobrado(prestamo.id));

  // --- Crear préstamo ---
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.persona || !form.monto) return;
    const ok = await run(() => q(supabase.from('prestamos').insert({
      user_id: userId,
      persona: form.persona,
      monto: parseFloat(form.monto),
      fecha_prestamo: form.fecha_prestamo,
      fecha_esperada: form.fecha_esperada || null,
      notas: form.notas || null,
      cobrado: false,
    })), loadData);
    if (ok) {
      setForm(formVacio());
      setShowForm(false);
    }
  };

  // --- Registrar cobro ---
  const handleCobro = async (e) => {
    e.preventDefault();
    if (!formCobro.monto || !cobroModal) return;
    const monto = parseFloat(formCobro.monto);
    const pendiente = getPendiente(cobroModal);
    if (monto > pendiente) {
      setError(`El cobro no puede superar lo pendiente (${formatCLP(pendiente)})`);
      return;
    }
    const prestamo = cobroModal;
    const ok = await run(async () => {
      const mov = formCobro.bolsillo_id
        ? await crearMovimiento({
            userId,
            bolsilloId: formCobro.bolsillo_id,
            tipo: 'ingreso',
            monto,
            descripcion: `Cobro préstamo: ${prestamo.persona}`,
            fecha: formCobro.fecha,
          })
        : null;
      try {
        await q(supabase.from('cobros_prestamo').insert({
          user_id: userId,
          prestamo_id: prestamo.id,
          bolsillo_id: formCobro.bolsillo_id || null,
          movimiento_id: mov?.id || null,
          monto,
          fecha: formCobro.fecha,
          nota: formCobro.nota || null,
        }));
      } catch (err) {
        await borrarMovimientoSimple(mov?.id);
        throw err;
      }
      await recalcularPrestamo(prestamo.id);
    }, loadData);
    if (ok) {
      setFormCobro({ monto: '', bolsillo_id: '', fecha: hoyISO(), nota: '' });
      setCobroModal(null);
    }
  };

  // --- Editar préstamo ---
  const handleEdit = (prestamo) => {
    setEditingId(prestamo.id);
    setEditForm({
      persona: prestamo.persona,
      monto: prestamo.monto,
      fecha_prestamo: prestamo.fecha_prestamo,
      fecha_esperada: prestamo.fecha_esperada || '',
      notas: prestamo.notas || '',
    });
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    const id = editingId;
    const ok = await run(async () => {
      await q(supabase.from('prestamos').update({
        persona: editForm.persona,
        monto: parseFloat(editForm.monto),
        fecha_prestamo: editForm.fecha_prestamo,
        fecha_esperada: editForm.fecha_esperada || null,
        notas: editForm.notas || null,
      }).eq('id', id));
      await recalcularPrestamo(id);
    }, loadData);
    if (ok) setEditingId(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este préstamo y todos sus cobros registrados?\n\nLos ingresos ya registrados en tus categorías se mantienen.')) return;
    await run(() => q(supabase.from('prestamos').delete().eq('id', id)), loadData);
  };

  const handleDeleteCobro = async (cobro) => {
    const msg = cobro.movimiento_id
      ? '¿Eliminar este cobro?\n\nTambién se eliminará el ingreso que se registró en la categoría.'
      : '¿Eliminar este cobro?';
    if (!window.confirm(msg)) return;
    await run(async () => {
      await q(supabase.from('cobros_prestamo').delete().eq('id', cobro.id));
      await borrarMovimientoSimple(cobro.movimiento_id);
      await recalcularPrestamo(cobro.prestamo_id);
    }, loadData);
  };

  // Deudas pendientes de una persona, de la más antigua a la más nueva.
  const pendientesDe = (persona) => prestamos
    .filter(p => !p.cobrado && p.persona === persona && getPendiente(p) > 0)
    .sort((a, b) => (a.fecha_prestamo || '').localeCompare(b.fecha_prestamo || ''));

  // Cuánto debe cada persona en total (los gastos compartidos crean un préstamo por gasto).
  const deudoresActivos = [...new Set(prestamos.filter(p => !p.cobrado).map(p => p.persona))]
    .map(persona => {
      const deudas = pendientesDe(persona);
      return { persona, cuentas: deudas.length, pendiente: deudas.reduce((s, p) => s + getPendiente(p), 0) };
    })
    .filter(d => d.pendiente > 0)
    .sort((a, b) => b.pendiente - a.pendiente);

  const abrirAbonoPersona = (persona) => {
    setFormAbono({ monto: '', bolsillo_id: bolsillos[0]?.id || '', fecha: hoyISO(), nota: '' });
    setAbonoPersona(persona);
  };

  // Un abono se va restando de las deudas de esa persona, empezando por la más antigua.
  const handleAbonoPersona = async (e) => {
    e.preventDefault();
    const persona = abonoPersona;
    const monto = parseFloat(formAbono.monto);
    const deudas = pendientesDe(persona);
    const totalPendiente = deudas.reduce((s, p) => s + getPendiente(p), 0);
    if (!monto || monto <= 0) return;
    if (monto > totalPendiente) {
      setError(`${persona} te debe ${formatCLP(totalPendiente)}: el abono no puede ser mayor.`);
      return;
    }
    const ok = await run(async () => {
      const mov = formAbono.bolsillo_id
        ? await crearMovimiento({
            userId,
            bolsilloId: formAbono.bolsillo_id,
            tipo: 'ingreso',
            monto,
            descripcion: `Abono de ${persona}`,
            fecha: formAbono.fecha,
          })
        : null;
      const creados = [];
      try {
        let restante = monto;
        for (const p of deudas) {
          if (restante <= 0) break;
          const aplica = Math.min(restante, getPendiente(p));
          const fila = await q(supabase.from('cobros_prestamo').insert({
            user_id: userId,
            prestamo_id: p.id,
            bolsillo_id: formAbono.bolsillo_id || null,
            movimiento_id: mov?.id || null,
            monto: aplica,
            fecha: formAbono.fecha,
            nota: formAbono.nota || `Abono de ${persona}`,
          }).select('id').single());
          creados.push({ id: fila.id, prestamoId: p.id });
          restante -= aplica;
        }
      } catch (err) {
        // Si falla a medio camino se deshace todo, para no dejar cobros a medias
        for (const c of creados) await supabase.from('cobros_prestamo').delete().eq('id', c.id);
        await borrarMovimientoSimple(mov?.id);
        throw err;
      }
      await Promise.all([...new Set(creados.map(c => c.prestamoId))].map(id => recalcularPrestamo(id)));
    }, loadData);
    if (ok) setAbonoPersona(null);
  };

  const prestamosActivos = prestamos.filter(p => !p.cobrado);
  const prestamosCobrados = prestamos.filter(p => p.cobrado);
  const totalPorCobrar = prestamosActivos.reduce((s, p) => s + getPendiente(p), 0);

  // Préstamos con fecha esperada próxima (dentro de 7 días o vencida)
  const prestamosProximos = prestamosActivos.filter(p => p.fecha_esperada && diasHasta(p.fecha_esperada) <= 7);

  if (loading) return (
    <div className="bolsillos-loading"><div className="loading-spinner"></div><p>Cargando...</p></div>
  );

  return (
    <div className="deudas-page">
      <div className="page-header">
        <div>
          <h2>Me Deben</h2>
          <p className="page-subtitle">Plata que prestaste y estás esperando recibir</p>
        </div>
        <button className="btn-primary" onClick={() => setShowForm(s => !s)}>
          {showForm ? 'Cancelar' : '+ Registrar préstamo'}
        </button>
      </div>

      <ErrorBanner error={error} onClose={() => setError('')} />

      {/* Resumen */}
      {prestamos.length > 0 && (
        <div className="summary-cards" style={{ marginBottom: 24 }}>
          <div className="card">
            <div className="card-icon">👥</div>
            <div className="card-content">
              <h4>Préstamos activos</h4>
              <p className="card-amount neutral">{prestamosActivos.length}</p>
            </div>
          </div>
          <div className="card">
            <div className="card-icon">💰</div>
            <div className="card-content">
              <h4>Total por cobrar</h4>
              <p className="card-amount positive">{formatCLP(totalPorCobrar)}</p>
            </div>
          </div>
          <div className="card">
            <div className="card-icon">✅</div>
            <div className="card-content">
              <h4>Cobrados</h4>
              <p className="card-amount positive">{prestamosCobrados.length}</p>
            </div>
          </div>
        </div>
      )}

      {/* Lo que debe cada persona: un abono se reparte entre todas sus deudas */}
      {deudoresActivos.length > 0 && (
        <div className="card panel" style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: '1rem', marginTop: 0, marginBottom: 12 }}>Lo que te debe cada persona</h3>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {deudoresActivos.map(d => (
              <div key={d.persona} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ flex: 1, minWidth: 140 }}>
                  <strong>{d.persona}</strong>
                  <span style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    {d.cuentas} cuenta{d.cuentas !== 1 ? 's' : ''} pendiente{d.cuentas !== 1 ? 's' : ''}
                  </span>
                </div>
                <strong className="text-success" style={{ fontSize: '1.05rem' }}>{formatCLP(d.pendiente)}</strong>
                <button className="btn-primary" style={{ fontSize: '0.85rem', padding: '8px 14px' }}
                  onClick={() => abrirAbonoPersona(d.persona)}>💰 Abonar</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Alerta de préstamos por vencer */}
      {prestamosProximos.length > 0 && (
        <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: '0.88rem', color: '#92400e' }}>
          ⏰ <strong>{prestamosProximos.length} préstamo{prestamosProximos.length > 1 ? 's' : ''}</strong> con fecha de cobro próxima o vencida:{' '}
          {prestamosProximos.map(p => p.persona).join(', ')}
        </div>
      )}

      {/* Formulario nuevo préstamo */}
      {showForm && (
        <div className="card" style={{ marginBottom: 24 }}>
          <h3 style={{ marginBottom: 16, fontSize: '1rem' }}>Registrar nuevo préstamo</h3>
          <form onSubmit={handleCreate}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
              <div className="form-group">
                <label>¿A quién le prestaste?</label>
                <input type="text" value={form.persona}
                  onChange={e => setForm({ ...form, persona: e.target.value })}
                  placeholder="Ej: Mi hermana, Ale, Juan..." required />
              </div>
              <div className="form-group">
                <label>¿Cuánto prestaste?</label>
                <input type="number" value={form.monto}
                  onChange={e => setForm({ ...form, monto: e.target.value })}
                  placeholder="Monto total prestado" required min="1" />
              </div>
              <div className="form-group">
                <label>Fecha del préstamo</label>
                <input type="date" value={form.fecha_prestamo}
                  onChange={e => setForm({ ...form, fecha_prestamo: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>¿Cuándo te pagan? (opcional)</label>
                <input type="date" value={form.fecha_esperada}
                  onChange={e => setForm({ ...form, fecha_esperada: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Notas (opcional)</label>
                <input type="text" value={form.notas}
                  onChange={e => setForm({ ...form, notas: e.target.value })}
                  placeholder="Ej: Para el pasaje, Me paga el 1ero..." />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? 'Guardando...' : 'Registrar préstamo'}
              </button>
              <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancelar</button>
            </div>
          </form>
        </div>
      )}

      {/* Lista vacía */}
      {prestamos.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)', background: 'white', borderRadius: 12 }}>
          <div style={{ fontSize: '3rem', marginBottom: 16 }}>🤝</div>
          <p style={{ fontWeight: 600, marginBottom: 8 }}>Sin préstamos registrados</p>
          <p style={{ fontSize: '0.9rem' }}>Cuando le prestes plata a alguien, regístralo acá para saber cuánto te deben en total.</p>
        </div>
      ) : (
        <>
          {/* Préstamos activos */}
          {prestamosActivos.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ marginBottom: 16, color: 'var(--text-secondary)', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: 1 }}>
                Pendientes de cobro
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {prestamosActivos.map(prestamo => {
                  const totalCobrado = getTotalCobrado(prestamo.id);
                  const pendiente = getPendiente(prestamo);
                  const cobrosDelPrestamo = cobros.filter(c => c.prestamo_id === prestamo.id);
                  const isExpanded = expandedId === prestamo.id;

                  const fechaEsp = prestamo.fecha_esperada;
                  const diasRestantes = fechaEsp ? diasHasta(fechaEsp) : null;
                  const estaVencido = diasRestantes !== null && diasRestantes < 0;
                  const estaProximo = diasRestantes !== null && diasRestantes >= 0 && diasRestantes <= 7;

                  if (editingId === prestamo.id) {
                    return (
                      <div key={prestamo.id} className="card">
                        <form onSubmit={handleSaveEdit}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
                            <div className="form-group"><label>¿A quién?</label>
                              <input type="text" value={editForm.persona} onChange={e => setEditForm({ ...editForm, persona: e.target.value })} required /></div>
                            <div className="form-group"><label>Monto</label>
                              <input type="number" value={editForm.monto} onChange={e => setEditForm({ ...editForm, monto: e.target.value })} required /></div>
                            <div className="form-group"><label>Fecha del préstamo</label>
                              <input type="date" value={editForm.fecha_prestamo} onChange={e => setEditForm({ ...editForm, fecha_prestamo: e.target.value })} /></div>
                            <div className="form-group"><label>Fecha esperada</label>
                              <input type="date" value={editForm.fecha_esperada} onChange={e => setEditForm({ ...editForm, fecha_esperada: e.target.value })} /></div>
                            <div className="form-group"><label>Notas</label>
                              <input type="text" value={editForm.notas} onChange={e => setEditForm({ ...editForm, notas: e.target.value })} /></div>
                          </div>
                          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                            <button type="submit" className="btn-primary" disabled={saving}>Guardar</button>
                            <button type="button" className="btn-secondary" onClick={() => setEditingId(null)}>Cancelar</button>
                          </div>
                        </form>
                      </div>
                    );
                  }

                  return (
                    <div key={prestamo.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                      {/* Encabezado clickeable */}
                      <div style={{ padding: '16px 20px', cursor: 'pointer' }}
                        onClick={() => setExpandedId(isExpanded ? null : prestamo.id)}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <span style={{ fontSize: '1.8rem', flexShrink: 0 }}>🤝</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 4 }}>
                              <div>
                                <p style={{ fontWeight: 700, margin: 0, color: 'var(--text)' }}>{prestamo.persona}</p>
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 2 }}>
                                  <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                                    Prestado el {formatFecha(prestamo.fecha_prestamo)}
                                  </span>
                                  {fechaEsp && (
                                    <span style={{
                                      fontSize: '0.75rem', fontWeight: 600, padding: '1px 7px', borderRadius: 99,
                                      background: estaVencido ? '#fee2e2' : estaProximo ? '#fef3c7' : '#f0fdf4',
                                      color: estaVencido ? '#b91c1c' : estaProximo ? '#92400e' : '#15803d',
                                    }}>
                                      {estaVencido
                                        ? `⚠ Vencido hace ${Math.abs(diasRestantes)} día${Math.abs(diasRestantes) !== 1 ? 's' : ''}`
                                        : diasRestantes === 0
                                        ? '⏰ Te pagan hoy'
                                        : estaProximo
                                        ? `⏰ Te pagan en ${diasRestantes} día${diasRestantes !== 1 ? 's' : ''}`
                                        : `📅 Te pagan el ${formatFecha(fechaEsp)}`}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                <p style={{ margin: 0, fontWeight: 800, fontSize: '1.1rem', color: '#16a34a' }}>{formatCLP(pendiente)}</p>
                                <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-muted)' }}>por cobrar</p>
                              </div>
                            </div>
                            <ProgressBar cobrado={totalCobrado} total={prestamo.monto} />
                            <div style={{ display: 'flex', gap: 16, fontSize: '0.8rem', color: 'var(--text-secondary)', flexWrap: 'wrap' }}>
                              <span>Prestado: <strong>{formatCLP(prestamo.monto)}</strong></span>
                              <span className="text-success">Cobrado: <strong>{formatCLP(totalCobrado)}</strong></span>
                            </div>
                            {prestamo.notas && <p style={{ margin: '4px 0 0', fontSize: '0.78rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>{prestamo.notas}</p>}
                          </div>
                          <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem', flexShrink: 0 }}>{isExpanded ? '▲' : '▼'}</span>
                        </div>
                      </div>

                      {/* Acciones y detalle expandido */}
                      {isExpanded && (
                        <div style={{ borderTop: '1px solid var(--border)', padding: '12px 20px', background: 'var(--bg)' }}>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                            <button className="btn-primary" style={{ fontSize: '0.85rem', padding: '8px 14px' }}
                              onClick={() => {
                                setCobroModal(prestamo);
                                setFormCobro({ monto: '', bolsillo_id: bolsillos[0]?.id || '', fecha: hoyISO(), nota: '' });
                              }}>
                              💰 Registrar cobro
                            </button>
                            <button className="btn-secondary" style={{ fontSize: '0.85rem', padding: '8px 12px' }} onClick={() => handleEdit(prestamo)}>✎ Editar</button>
                            <button className="btn-secondary" style={{ fontSize: '0.85rem', padding: '8px 12px', color: 'var(--danger)' }} onClick={() => handleDelete(prestamo.id)}>× Eliminar</button>
                          </div>

                          {cobrosDelPrestamo.length > 0 && (
                            <div>
                              <p style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Historial de cobros
                              </p>
                              {cobrosDelPrestamo.map(cobro => {
                                const bolsillo = bolsillos.find(b => b.id === cobro.bolsillo_id);
                                return (
                                  <div key={cobro.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: '0.85rem' }}>
                                    <div>
                                      <span style={{ color: 'var(--text)' }}>{formatFecha(cobro.fecha)}</span>
                                      {bolsillo && <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>→ {bolsillo.nombre}</span>}
                                      {cobro.nota && <span style={{ color: 'var(--text-muted)', marginLeft: 8, fontStyle: 'italic' }}>· {cobro.nota}</span>}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                      <span className="text-success" style={{ fontWeight: 600 }}>+{formatCLP(cobro.monto)}</span>
                                      <button className="btn-icon-del small" onClick={() => handleDeleteCobro(cobro)} title="Eliminar cobro">×</button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Préstamos cobrados */}
          {prestamosCobrados.length > 0 && (
            <div>
              <h3 style={{ marginBottom: 12, color: 'var(--text-secondary)', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: 1 }}>
                Cobrados ✅
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {prestamosCobrados.map(prestamo => (
                  <div key={prestamo.id} className="card" style={{ opacity: 0.7, display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px' }}>
                    <span style={{ fontSize: '1.4rem' }}>🤝</span>
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: 0, fontWeight: 600 }}>{prestamo.persona}</p>
                      <p style={{ margin: 0, fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                        {formatCLP(prestamo.monto)} — cobrado en su totalidad
                      </p>
                    </div>
                    <button className="btn-secondary" style={{ fontSize: '0.78rem', padding: '4px 10px', color: 'var(--danger)' }}
                      onClick={() => handleDelete(prestamo.id)}>× Eliminar</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal: Abono de una persona (se reparte entre sus deudas) */}
      {abonoPersona && (() => {
        const deudas = pendientesDe(abonoPersona);
        const totalPendiente = deudas.reduce((s, p) => s + getPendiente(p), 0);
        let porRepartir = parseFloat(formAbono.monto) || 0;
        const reparto = deudas.map(p => {
          const aplica = Math.min(porRepartir, getPendiente(p));
          porRepartir -= aplica;
          return { p, aplica };
        }).filter(r => r.aplica > 0);
        return (
          <div className="modal-overlay" onClick={() => setAbonoPersona(null)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>💰 Abono de {abonoPersona}</h3>
                <button className="modal-close" onClick={() => setAbonoPersona(null)}>×</button>
              </div>
              <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: '0.88rem', color: 'var(--text-secondary)' }}>
                Te debe <strong style={{ color: '#16a34a' }}>{formatCLP(totalPendiente)}</strong> en {deudas.length} cuenta{deudas.length !== 1 ? 's' : ''}.
                El abono se resta empezando por la deuda más antigua.
              </div>
              <form onSubmit={handleAbonoPersona}>
                <div className="form-group">
                  <label>¿Cuánto te abonó?</label>
                  <input type="number" value={formAbono.monto}
                    onChange={e => setFormAbono({ ...formAbono, monto: e.target.value })}
                    placeholder="0" required min="1" max={totalPendiente} autoFocus />
                  <span className="field-hint">Máximo: {formatCLP(totalPendiente)}</span>
                </div>
                <div className="form-group">
                  <label>¿A qué categoría entra el dinero?</label>
                  <select value={formAbono.bolsillo_id}
                    onChange={e => setFormAbono({ ...formAbono, bolsillo_id: e.target.value })}>
                    <option value="">No agregar a ninguna categoría</option>
                    {bolsillos.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
                  </select>
                  {formAbono.bolsillo_id && <span className="field-hint">Se creará un ingreso en esa categoría automáticamente</span>}
                </div>
                <div className="form-group">
                  <label>Fecha</label>
                  <input type="date" value={formAbono.fecha}
                    onChange={e => setFormAbono({ ...formAbono, fecha: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Nota (opcional)</label>
                  <input type="text" value={formAbono.nota}
                    onChange={e => setFormAbono({ ...formAbono, nota: e.target.value })}
                    placeholder="Ej: Transferencia, Me pagó la mitad..." />
                </div>
                {reparto.length > 0 && (
                  <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: '0.85rem', display: 'grid', gap: 4 }}>
                    <strong style={{ fontSize: '0.8rem' }}>Así se reparte:</strong>
                    {reparto.map(r => (
                      <div key={r.p.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                        <span>{r.p.notas || `Préstamo del ${formatFecha(r.p.fecha_prestamo)}`}</span>
                        <strong className="text-success">−{formatCLP(r.aplica)}</strong>
                      </div>
                    ))}
                  </div>
                )}
                <div className="modal-actions">
                  <button type="button" className="btn-secondary" onClick={() => setAbonoPersona(null)}>Cancelar</button>
                  <button type="submit" className="btn-primary" disabled={saving}>
                    {saving ? 'Guardando...' : '💰 Registrar abono'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        );
      })()}

      {/* Modal: Registrar cobro */}
      {cobroModal && (
        <div className="modal-overlay" onClick={() => setCobroModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>💰 Cobro de "{cobroModal.persona}"</h3>
              <button className="modal-close" onClick={() => setCobroModal(null)}>×</button>
            </div>
            <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: '0.88rem', color: 'var(--text-secondary)' }}>
              Por cobrar: <strong style={{ color: '#16a34a' }}>{formatCLP(getPendiente(cobroModal))}</strong>
            </div>
            <form onSubmit={handleCobro}>
              <div className="form-group">
                <label>Monto cobrado</label>
                <input type="number" value={formCobro.monto}
                  onChange={e => setFormCobro({ ...formCobro, monto: e.target.value })}
                  placeholder="0" required min="1" autoFocus />
              </div>
              <div className="form-group">
                <label>¿A qué categoría entra el dinero?</label>
                <select value={formCobro.bolsillo_id}
                  onChange={e => setFormCobro({ ...formCobro, bolsillo_id: e.target.value })}>
                  <option value="">No agregar a ninguna categoría</option>
                  {bolsillos.map(b => (
                    <option key={b.id} value={b.id}>{b.nombre}</option>
                  ))}
                </select>
                {formCobro.bolsillo_id && (
                  <span className="field-hint">Se creará un ingreso en esa categoría automáticamente</span>
                )}
              </div>
              <div className="form-group">
                <label>Fecha</label>
                <input type="date" value={formCobro.fecha}
                  onChange={e => setFormCobro({ ...formCobro, fecha: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Nota (opcional)</label>
                <input type="text" value={formCobro.nota}
                  onChange={e => setFormCobro({ ...formCobro, nota: e.target.value })}
                  placeholder="Ej: Me pagó la mitad, Transferencia..." />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setCobroModal(null)}>Cancelar</button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Guardando...' : '💰 Registrar cobro'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default MeDeben;
